import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import NotificationSchema, {
  DEFAULT_OCCURRENCE_COUNT,
  NotificationJSON,
  NotificationParams,
} from "notifications/_schemas/notification-schema";
import { Action } from "notifications/notification-action";
import NotificationMessage from "notifications/messages/notification-message";
import NotificationRecipient from "notifications/recipients/notification-recipient";
import NotificationTopic from "notifications/topics/notification-topic";
import RecipientName from "notifications/recipients/recipient-name";
import SenderName from "notifications/senders/sender-name";
import { isKnownNotificationAction } from "notifications/notification-action-link";

export default class Notification {
  public static create(values: NotificationParams): Notification {
    return new Notification(values);
  }

  public static fromJSON(value: NotificationJSON): Notification {
    const parsed = NotificationSchema.deserialize(value);
    return new Notification(parsed, Notification.hasUnrecognizedActionRequired(value));
  }

  public static seenByUserAt(notification?: Notification, userId?: Guid): AHDate | null {
    if (!(notification instanceof Notification)) return null;
    if (!userId) {
      console.warn("User ID is undefined. Cannot get seen date.");
      return null;
    }
    if (notification.senderUserId?.isEqualTo(userId)) return notification.createdDate;
    const recipient = notification.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.seenAt : null;
  }

  public static isSeenByUser(notification?: Notification, userId?: Guid): boolean {
    if (!(notification instanceof Notification)) return false;
    if (!userId) {
      console.warn("User ID is undefined. Cannot check if notification is seen.");
      return false;
    }
    if (notification.senderUserId?.isEqualTo(userId)) return true;
    const recipient = notification.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.isSeen : false;
  }

  public static userIsRecipient(notification?: Notification, userId?: Guid): boolean {
    if (!(notification instanceof Notification)) return false;
    if (!userId) {
      console.warn("User ID is undefined. Cannot check if notification has recipient.");
      return false;
    }
    return notification.recipients.some((recipient) => recipient.userId.isEqualTo(userId));
  }

  public readonly id: Guid;
  public readonly recipients: NotificationRecipient[] = [];
  public readonly senderUserId?: Guid;
  public readonly message?: NotificationMessage;
  /**
   * How many events this one notification stands for (#830).
   *
   * The API folds a run of new messages in the same forum into the notification already sitting
   * unread in the recipient's inbox instead of sending one row per message, and counts them here.
   * Anything that has never been aggregated — every older payload, and every category that is never
   * aggregated at all — counts as {@link DEFAULT_OCCURRENCE_COUNT}, so a reader can always take this
   * as a number rather than testing for its absence.
   */
  public readonly occurrenceCount: number;
  public readonly topic: NotificationTopic;
  public readonly actionRequired?: Action;
  /**
   * `actionRequired` is deliberately dropped by schema validation when a newer API sends an
   * action this client does not know. Keep that fact so routing does not mistake it for a
   * notification that was sent with no action at all.
   */
  public readonly hasUnrecognizedActionRequired: boolean;
  public readonly actionLink?: unknown;
  public readonly createdDate: AHDate;
  public readonly isCanceled?: boolean;
  public readonly senderName?: SenderName;
  public readonly recipientName?: RecipientName;
  public readonly subheader?: string;
  public readonly body?: string;
  public readonly finePrint?: string;
  /**
   * Whether the reader archived it, as the API answers for the reader asking. The Communications page
   * lists Sent and Archived together (#6719), so a row says which box it came from. The API always
   * sends it, realtime payloads included; it is absent only on a row cached before #6719.
   */
  public readonly isArchived?: boolean;

  public constructor(values: NotificationParams, hasUnrecognizedActionRequired?: boolean) {
    const validatedValues = NotificationSchema.validate(values);
    this.id = validatedValues.id;
    this.recipients = validatedValues.recipients;
    this.senderUserId = validatedValues.senderUserId;
    this.message = validatedValues.message;
    this.occurrenceCount = validatedValues.occurrenceCount ?? DEFAULT_OCCURRENCE_COUNT;
    this.topic = validatedValues.topic;
    this.actionRequired = validatedValues.actionRequired;
    this.hasUnrecognizedActionRequired = hasUnrecognizedActionRequired ?? Notification.hasUnrecognizedActionRequired(values);
    this.actionLink = validatedValues.actionLink;
    this.createdDate = validatedValues.createdDate ?? new AHDate();
    this.isCanceled = validatedValues.isCanceled;
    this.senderName = validatedValues.senderName;
    this.recipientName = validatedValues.recipientName;
    this.subheader = validatedValues.subheader;
    this.body = validatedValues.body;
    this.finePrint = validatedValues.finePrint;
    this.isArchived = validatedValues.isArchived;
  }

  public toJSON(): NotificationJSON | null | undefined {
    return NotificationSchema.serialize(this);
  }

  public seenByUserAt(userId?: Guid): AHDate | null {
    return Notification.seenByUserAt(this, userId);
  }

  public isSeenByUser(userId?: Guid): boolean {
    return Notification.isSeenByUser(this, userId);
  }

  public markSeenForUser(userId?: Guid): void {
    if (!userId) {
      console.warn("User ID is undefined. Cannot mark notification as seen.");
      return;
    }
    const recipientIndex = this.recipients.findIndex((recipient) => recipient.userId.isEqualTo(userId));
    if (recipientIndex >= 0) {
      const updatedRecipient = NotificationRecipient.create({
        userId: this.recipients[recipientIndex].userId,
        seenAt: new AHDate(),
      });
      this.recipients[recipientIndex] = updatedRecipient;
    } else {
      console.warn(`User with ID ${userId.value} is not a recipient of this notification.`);
    }
  }

  public userIsRecipient(userId: Guid | undefined) {
    return Notification.userIsRecipient(this, userId);
  }

  /**
   * Two notifications are the same notification when they carry the same id *and* stand for the same
   * number of occurrences. An aggregation (#830) keeps the id and raises the count, so leaving the
   * count out here would make the third message in a thread compare equal to the second and never
   * reach the screen.
   */
  public isEqualTo(other: Notification | null | undefined): boolean {
    return this.id.isEqualTo(other?.id) && this.occurrenceCount === other?.occurrenceCount;
  }

  public toString(): string {
    return `Notification(${this.id.value})`;
  }

  private static hasUnrecognizedActionRequired(value: unknown): boolean {
    if (!value || typeof value !== "object") return false;
    const actionRequired = (value as { actionRequired?: unknown }).actionRequired;
    return typeof actionRequired === "string" && !isKnownNotificationAction(actionRequired);
  }
}
